import { FC, useContext } from 'react';
import track from 'react-tracking';
import BreadcrumbSection from './BreadcrumbSection';
import BreadcrumbDivider from './BreadcrumbDivider';
import GenericRender from './GenericRender';
import DocumentSchema from '@components/DocumentSchema';
import type { BreadcrumbList, ListItem, URL } from 'schema-dts';
import { SWS_HOST } from '@/constants';
import { slugify } from '@utilities/slugify';
import { Context as OriginContext } from '@/context/Origin';
import { actionToPath, LocationState } from 'redux-first-router';
import { useSelector, shallowEqual } from 'react-redux';
import queryString from 'query-string';

export interface BreadcrumbItem {
  name: string;
  canonicalUrl?: string;
  dispatch?: ReducerAction;
  documentSchemaOnly?: boolean;
}

interface BreadcrumbProps {
  list: BreadcrumbItem[];
}

const Breadcrumb: FC<BreadcrumbProps & Tracking> = ({ list }) => {
  const routesMap = useSelector(
    (state: any) => (state.location as LocationState).routesMap,
    shallowEqual
  );
  const origin = useContext(OriginContext);
  return (
    <ul>
      <DocumentSchema<BreadcrumbList>
        item={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: list
            .filter(({ name }) => !!name)
            .map(({ name, canonicalUrl, dispatch }, i) => {
              const listItem = {
                '@type': 'ListItem',
                position: i + 1,
                name: name,
              };
              if (canonicalUrl) {
                /** Use canonicalUrl */
                listItem['item'] = `${SWS_HOST}${canonicalUrl}` as URL;
              } else {
                try {
                  /** Derive item from route action */
                  const path = actionToPath(
                    dispatch as any,
                    routesMap,
                    queryString
                  );
                  listItem['item'] = `${origin}${path}`;
                } catch (e) {
                  // 🤷‍♂️ boo-hoo meta data is broken it's the end of the world
                }
              }
              return listItem;
            }) as ListItem[],
        }}
      />
      {list
        .filter((value) => !value.documentSchemaOnly)
        .map((value, index, self) => {
          const isLast = self.length - 1 === index;
          const isSecondToLast = self.length - 2 === index && self.length > 1;
          return (
            <GenericRender
              key={`Breadcrumb-${slugify(value.name)}`}
              value={value}
              index={index}
              isLast={isLast}
              isSecondToLast={isSecondToLast}
              isOnlyOne={self.length === 1}
            />
          );
        })}
    </ul>
  );
};

Breadcrumb.defaultProps = {
  list: [],
};
export { BreadcrumbSection as Section, BreadcrumbDivider as Divider };

export default track(({ list }) => ({
  type: 'track',
  subject: 'breadcrumb',
  breadcrumb_length: list ? list.length : 0,
}))(Breadcrumb);
